import React from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  IconButton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";

interface ComingSoonPopupProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
}

const ComingSoonPopup: React.FC<ComingSoonPopupProps> = ({
  open,
  onClose,
  title = "Coming Soon",
  description = "This feature is almost ready. We're putting on the finishing touches — stay tuned!",
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          background: "#0f172a",
          color: "#fff",
          textAlign: "center",
          p: { xs: 2, sm: 3 },
          overflow: "hidden",
          border: "0.5px solid rgba(75,165,235,0.25)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "-60px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "180px",
            height: "180px",
            background:
              "radial-gradient(circle, rgba(75,165,235,0.15) 0%, transparent 70%)",
            pointerEvents: "none",
          },
        },
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={onClose}
        size="small"
        aria-label="Close"
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          width: 30,
          height: 30,
          border: "0.5px solid rgba(255,255,255,0.12)",
          background: "rgba(255,255,255,0.06)",
          color: "rgba(255,255,255,0.45)",
          "&:hover": {
            background: "rgba(255,255,255,0.12)",
            color: "rgba(255,255,255,0.8)",
          },
        }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>

      <DialogContent sx={{ px: { xs: 1, sm: 2 }, pt: 2 }}>
        {/* Rocket icon */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            background: "rgba(75,165,235,0.12)",
            border: "1px solid rgba(75,165,235,0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
            "@keyframes float": {
              "0%, 100%": { transform: "translateY(0px)" },
              "50%": { transform: "translateY(-6px)" },
            },
            animation: "float 3s ease-in-out infinite",
          }}
        >
          <RocketLaunchIcon sx={{ fontSize: 32, color: "#4BA5EB" }} />
        </Box>

        {/* Badge */}
        <Box
          component="span"
          sx={{
            display: "inline-block",
            fontSize: "11px",
            fontWeight: 500,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "#4BA5EB",
            background: "rgba(75,165,235,0.1)",
            border: "0.5px solid rgba(75,165,235,0.3)",
            borderRadius: "100px",
            px: 1.5,
            py: 0.5,
            mb: 1.5,
          }}
        >
          In development
        </Box>

        {/* Title */}
        <Typography
          sx={{
            fontSize: { xs: "20px", sm: "26px" },
            fontWeight: 500,
            mb: 0.75,
            color: "#f8fafc",
            lineHeight: 1.3,
            textAlign: "center",
          }}
        >
          {title}
        </Typography>

        {/* Description */}
        <Typography
          sx={{
            fontSize: "14px",
            color: "rgba(255,255,255,0.5)",
            lineHeight: 1.7,
            mb: 2.5,
            px: 0.5,
          }}
        >
          {description}
        </Typography>

        {/* Animated dots */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "6px",
            mb: 2.5,
          }}
        >
          {[0, 0.2, 0.4].map((delay, i) => (
            <Box
              key={i}
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "rgba(75,165,235,0.25)",
                "@keyframes dotPulse": {
                  "0%, 100%": {
                    background: "rgba(75,165,235,0.2)",
                    transform: "scale(1)",
                  },
                  "50%": {
                    background: "rgba(75,165,235,0.8)",
                    transform: "scale(1.3)",
                  },
                },
                animation: `dotPulse 1.5s ease-in-out ${delay}s infinite`,
              }}
            />
          ))}
        </Box>

        {/* Button */}
        <Button
          fullWidth
          onClick={onClose}
          startIcon={<CheckIcon />}
          sx={{
            background: "linear-gradient(135deg, #0168B5 0%, #4BA5EB 100%)",
            color: "#fff",
            borderRadius: "12px",
            py: 1.4,
            textTransform: "none",
            fontWeight: 500,
            fontSize: "15px",
            letterSpacing: "0.01em",
            transition: "opacity 0.2s, transform 0.15s",
            "&:hover": {
              background: "linear-gradient(135deg, #0168B5 0%, #4BA5EB 100%)",
              opacity: 0.88,
            },
            "&:active": {
              transform: "scale(0.98)",
            },
          }}
        >
          Got it
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default ComingSoonPopup;